// Phase I3 — Corners (Territorial) Intelligence panel.
// Maps super_acca_territorial → DNAStat tiles + tactical matchup.
// Empty-state when sample_valid=false.

import { Badge, DNAStat, Eyebrow } from "../../../components/super-acca";
import { CATEGORY } from "../../../super-acca/categories";
import type { TerritorialIntelView } from "../../../super-acca/view-models";

export interface CornersIntelPanelProps {
  data: TerritorialIntelView | null;
}

export function CornersIntelPanel({ data }: CornersIntelPanelProps) {
  if (!data || !data.sample_valid) {
    return (
      <div
        style={{
          padding: 22,
          background: "var(--sa-bg-panel)",
          border: "1px solid var(--sa-hairline)",
          borderRadius: 4,
        }}
      >
        <Eyebrow color={CATEGORY.TERRITORY.color}>Corners Intelligence</Eyebrow>
        <p
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            marginTop: 12,
            lineHeight: 1.6,
          }}
        >
          Corners model awaits settled-fixture corner counts. Territorial
          intelligence activates once both clubs have ≥10 corner-tagged
          fixtures in the dataset for the active season.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 22,
        background: "var(--sa-bg-panel)",
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Eyebrow color={CATEGORY.TERRITORY.color}>Corners Intelligence</Eyebrow>
          {data.tactical_matchup ? (
            <div
              style={{
                fontFamily: "var(--sa-font-display)",
                fontSize: 22,
                fontWeight: 600,
                marginTop: 6,
                color: "var(--sa-fg-primary)",
              }}
            >
              Tactical matchup detected · {data.tactical_matchup.replace(/_/g, " ")}
            </div>
          ) : null}
        </div>
        {data.matchup_uplift !== null ? (
          <Badge variant="success">
            UPLIFT +{data.matchup_uplift} CORNERS
          </Badge>
        ) : null}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 14,
          marginTop: 18,
        }}
      >
        <DNAStat label="EXPECTED TOTAL" value={data.expected_total_corners ?? "—"} accent />
        <DNAStat label="FIRST HALF" value={data.expected_first_half_corners ?? "—"} />
        <DNAStat
          label="EARLY (10′)"
          value={
            data.early_corners_10min_prob !== null
              ? `${(data.early_corners_10min_prob * 100).toFixed(0)}%`
              : "—"
          }
        />
        <DNAStat label="AH RECOMMEND" value={data.asian_corners_recommendation ?? "—"} />
      </div>
      <div
        style={{
          marginTop: 14,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 14,
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11.5,
        }}
      >
        <Mini label="HOME AVG" value={data.home_corners_avg ?? "—"} />
        <Mini label="AWAY AVG" value={data.away_corners_avg ?? "—"} />
        <Mini label="H2H AVG" value={data.h2h_avg ?? "—"} />
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        background: "var(--sa-bg-raised)",
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
      }}
    >
      <span
        style={{
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.16em",
          fontSize: 9.5,
        }}
      >
        {label}
      </span>
      <span style={{ color: "var(--sa-fg-primary)", marginLeft: 10 }}>{value}</span>
    </div>
  );
}
